/**
 * Authentication Context and Hook
 * Provides authentication state management throughout the application
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import authService from '../services/authService';
// DISABLED: Auto-room creation removed per TODO #10
// import autoRoomService from '../services/autoRoomService';
import type {
  User,
  AuthTokens,
  AuthState,
  LoginCredentials,
  RegisterData,
  ChangePasswordData,
  UpdateProfileData
} from '../types/auth';

// Action types for auth reducer
type AuthAction =
  | { type: 'AUTH_LOADING' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; tokens: AuthTokens } }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'AUTH_CLEAR_ERROR' }
  | { type: 'AUTH_UPDATE_USER'; payload: User };

// Initial auth state
const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

// Auth reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_LOADING':
      return {
        ...state,
        isLoading: true,
        error: null
      };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        tokens: action.payload.tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };

    case 'AUTH_ERROR':
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      };

    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };

    case 'AUTH_CLEAR_ERROR':
      return {
        ...state,
        error: null
      };

    case 'AUTH_UPDATE_USER':
      return {
        ...state,
        user: action.payload
      };

    default:
      return state;
  }
};

// Auth context interface
interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<boolean>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (profileData: UpdateProfileData) => Promise<void>;
  changePassword: (passwordData: ChangePasswordData) => Promise<void>;
  clearError: () => void;
  refreshUserProfile: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider component
interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize authentication state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('[AuthProvider] Initializing auth...');
        dispatch({ type: 'AUTH_LOADING' });

        const { user, tokens } = authService.initializeAuth();
        console.log('[AuthProvider] initializeAuth result:', { 
          hasUser: !!user, 
          userId: user?._id,
          hasTokens: !!tokens,
          userObj: user
        });

        if (user && tokens) {
          // Verify token is still valid by fetching fresh profile
          try {
            console.log('[AuthProvider] Fetching fresh profile...');
            const response = await authService.getProfile();
            console.log('[AuthProvider] Profile response:', response);
            
            if (response.success && response.data) {
              console.log('[AuthProvider] Profile data:', response.data);
              console.log('[AuthProvider] Profile user:', response.data.user);
              console.log('[AuthProvider] User _id:', response.data.user._id);
              console.log('[AuthProvider] User id:', (response.data.user as any).id);
              console.log('[AuthProvider] Full user keys:', Object.keys(response.data.user));
              dispatch({
                type: 'AUTH_SUCCESS',
                payload: {
                  user: response.data.user,
                  tokens
                }
              });
            } else {
              // Token invalid, clear auth data
              console.log('[AuthProvider] Profile fetch failed, clearing auth');
              authService.clearAuthData();
              dispatch({ type: 'AUTH_LOGOUT' });
            }
          } catch (error) {
            // Profile fetch failed, clear auth data
            console.error('[AuthProvider] Profile fetch error:', error);
            authService.clearAuthData();
            dispatch({ type: 'AUTH_LOGOUT' });
          }
        } else {
          console.log('[AuthProvider] No user or tokens, logging out');
          dispatch({ type: 'AUTH_LOGOUT' });
        }
      } catch (error) {
        console.error('[AuthProvider] Auth initialization error:', error);
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      dispatch({ type: 'AUTH_LOADING' });

      const response = await authService.login(credentials);

      if (response.success && response.data) {
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: response.data.user,
            tokens: response.data.tokens
          }
        });
        return true;
      } else {
        dispatch({
          type: 'AUTH_ERROR',
          payload: response.message || 'Invalid credentials'
        });
        return false;
      }
    } catch (error) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: error instanceof Error ? error.message : 'Login failed'
      });
      return false;
    }
  }, []);

  // Register function
  const register = useCallback(async (userData: RegisterData): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_LOADING' });

      const response = await authService.register(userData);

      if (response.success && response.data) {
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: response.data.user,
            tokens: response.data.tokens
          }
        });

        // DISABLED: Auto-room creation removed per TODO #10
        // Create auto-room for new user registration
        // try {
        //   const autoRoomIds = await autoRoomService.createAutoRoomsOnLogin(response.data.user.username);
        //   if (autoRoomIds.length > 0) {
        //     console.log('✅ Auto-created welcome room for new user');
        //   }
        // } catch (autoRoomError) {
        //   console.warn('Auto-room creation failed for new user:', autoRoomError);
        //   // Don't fail registration if auto-room creation fails
        // }
      } else {
        dispatch({
          type: 'AUTH_ERROR',
          payload: response.message || 'Registration failed'
        });
      }
    } catch (error) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: error instanceof Error ? error.message : 'Registration failed'
      });
    }
  }, []);

  // Logout function
  const logout = useCallback(async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  }, []);

  // Update profile function
  const updateProfile = useCallback(async (profileData: UpdateProfileData): Promise<void> => {
    try {
      const response = await authService.updateProfile(profileData);

      if (response.success && response.data) {
        dispatch({
          type: 'AUTH_UPDATE_USER',
          payload: response.data.user
        });
      } else {
        throw new Error(response.message || 'Profile update failed');
      }
    } catch (error) {
      throw error; // Re-throw to handle in component
    }
  }, []);

  // Change password function
  const changePassword = useCallback(async (passwordData: ChangePasswordData): Promise<void> => {
    try {
      const response = await authService.changePassword(passwordData);

      if (!response.success) {
        throw new Error(response.message || 'Password change failed');
      }
    } catch (error) {
      throw error; // Re-throw to handle in component
    }
  }, []);

  // Clear error function
  const clearError = useCallback((): void => {
    dispatch({ type: 'AUTH_CLEAR_ERROR' });
  }, []);

  // Refresh user profile
  const refreshUserProfile = useCallback(async (): Promise<void> => {
    try {
      const response = await authService.getProfile();

      if (response.success && response.data) {
        dispatch({
          type: 'AUTH_UPDATE_USER',
          payload: response.data.user
        });
      }
    } catch (error) {
      console.error('Profile refresh error:', error);
    }
  }, []);

  // Context value
  const contextValue: AuthContextType = useMemo(() => ({
    ...state,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    clearError,
    refreshUserProfile
  }), [
    state,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    clearError,
    refreshUserProfile
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

// HOC for protected routes
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-lg">Loading...</div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-lg text-red-600">Please log in to access this page.</div>
        </div>
      );
    }

    return <Component {...props} />;
  };
};

export default useAuth;